"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

interface NavItem {
    href: string;
    icon: string;
    label: string;
    badge?: number;
}

const mainNav: (NavItem & { tooltip: string })[] = [
    { href: "/", icon: "📊", label: "Dashboard", tooltip: "Panel principal con resumen de documentos procesados, estados y estadísticas" },
    { href: "/upload", icon: "📤", label: "Subir documento", tooltip: "Subir un documento para clasificación y extracción automática de datos" },
    { href: "/documents", icon: "📄", label: "Procesado", tooltip: "Explorar y gestionar todos los documentos procesados por el sistema" },
    { href: "/review", icon: "📋", label: "Revisión", tooltip: "Revisar documentos pendientes que requieren verificación manual" },
    { href: "/databases", icon: "🗄️", label: "Bases de datos", tooltip: "Vista tabular de documentos procesados agrupados por tipo de comprobante" },
];

const finanzasNav: (NavItem & { tooltip: string })[] = [
    { href: "/finanzas/hoy", icon: "🏠", label: "Hoy", tooltip: "Cuánto tengo, cuánto debo, si me alcanza y qué hacer hoy — se lee en 30 segundos" },
    { href: "/finanzas", icon: "💰", label: "Finanzas", tooltip: "Cash flow, costos por tipo, vencimientos y saldos bancarios del grupo" },
    { href: "/finanzas/carga", icon: "✍️", label: "Carga manual", tooltip: "Cargar saldos bancarios, movimientos sin comprobante e importar ventas" },
    { href: "/finanzas/kpis", icon: "📈", label: "Tablero", tooltip: "Foto financiera del día: posición, deuda, margen, cashflow y alertas" },
    { href: "/finanzas/posicion", icon: "🏦", label: "Posición y bancos", tooltip: "Saldos por cuenta, tenencias, extractos y movimientos bancarios" },
    { href: "/finanzas/deuda", icon: "📉", label: "Deuda", tooltip: "Cuánto debo por componente y por vencimiento, y cuánto necesito vender para cubrirlo" },
    { href: "/finanzas/tarjetas", icon: "💳", label: "Tarjetas", tooltip: "Fichas por tarjeta, proyección de cuotas y movimientos por reconocer" },
    { href: "/finanzas/tarjetas-motor", icon: "⚙️", label: "Tarjetas: el motor", tooltip: "La tarjeta como capital de trabajo: pasivo, en qué se va y qué cuesta la línea" },
    { href: "/finanzas/operacion", icon: "🛠️", label: "Operación", tooltip: "Salud del pipeline diario, corridas de agentes, fuentes y brief de Telegram" },
    { href: "/finanzas/conciliacion", icon: "🔗", label: "Conciliación", tooltip: "Cola de propuestas de conciliación bancaria, confirmar/rechazar/deshacer" },
    { href: "/finanzas/alertas", icon: "🚨", label: "Alertas", tooltip: "Cola de alertas del motor de negocio, con acciones de resolución" },
    { href: "/finanzas/confianza", icon: "🧭", label: "Confianza", tooltip: "Qué puede afirmar el sistema hoy: trazabilidad por cuenta y lo que no cierra" },
    { href: "/finanzas/margen", icon: "🧮", label: "Margen", tooltip: "Costo por venta en tres capas (estimado, reposición y real) y margen de caja" },
    { href: "/finanzas/fiscal", icon: "🏛️", label: "Fiscal", tooltip: "Obligaciones AFIP/AGIP, planes de pago y notificaciones del estudio contable" },
    { href: "/finanzas/reportes", icon: "📑", label: "Reportes", tooltip: "Reportes semanales/mensuales, previsualización y publicación en Rocky Hub" },
    { href: "/finanzas/agentes", icon: "🤖", label: "Agentes", tooltip: "Tesorero, Controller, Auditor y consumo de IA de los agentes financieros" },
    { href: "/finanzas/config", icon: "🔧", label: "Configuración admin", tooltip: "Parámetros, cron, conectores, agentes de IA, tipos de alerta y API keys M2M" },
];

const manageNav: (NavItem & { tooltip: string })[] = [
    { href: "/dictionaries", icon: "📖", label: "Diccionarios", tooltip: "Gestionar las entidades propias y alias para clasificación inteligente" },
    { href: "/audit", icon: "🔍", label: "Auditoría", tooltip: "Registro de todas las acciones realizadas en el sistema" },
    { href: "/qa", icon: "✅", label: "QA", tooltip: "Control de calidad y métricas de precisión del clasificador" },
    { href: "/settings", icon: "⚙️", label: "Configuración", tooltip: "Configurar modelos de IA, claves API, Telegram, Drive y más" },
];

export default function Sidebar() {
    const pathname = usePathname();

    return (
        <aside className="sidebar" id="sidebar">
            {/* Logo */}
            <div className="sidebar-logo">
                <div className="sidebar-logo-icon">🗂️</div>
                <div className="sidebar-logo-text">
                    Clasificador
                    <span>Administrativo</span>
                </div>
            </div>

            {/* Navigation */}
            <nav className="sidebar-nav">
                <div className="sidebar-section-label">Principal</div>
                {mainNav.map((item) => (
                    <Link
                        key={item.href}
                        href={item.href}
                        className={`sidebar-link ${pathname === item.href ? "active" : ""}`}
                        id={`nav-${item.href.replace("/", "") || "dashboard"}`}
                        title={item.tooltip}
                    >
                        <span className="sidebar-link-icon">{item.icon}</span>
                        {item.label}
                        {item.badge !== undefined && item.badge > 0 && (
                            <span className="sidebar-link-badge">{item.badge}</span>
                        )}
                    </Link>
                ))}

                <div className="sidebar-section-label">Administración</div>
                {finanzasNav.map((item) => (
                    <Link
                        key={item.href}
                        href={item.href}
                        className={`sidebar-link ${pathname === item.href ? "active" : ""}`}
                        id={`nav-finanzas-${item.href.split("/").pop()}`}
                        title={item.tooltip}
                    >
                        <span className="sidebar-link-icon">{item.icon}</span>
                        {item.label}
                    </Link>
                ))}

                <div className="sidebar-section-label">Gestión</div>
                {manageNav.map((item) => (
                    <Link
                        key={item.href}
                        href={item.href}
                        className={`sidebar-link ${pathname === item.href ? "active" : ""}`}
                        id={`nav-${item.href.replace("/", "")}`}
                        title={item.tooltip}
                    >
                        <span className="sidebar-link-icon">{item.icon}</span>
                        {item.label}
                        {item.badge !== undefined && item.badge > 0 && (
                            <span className="sidebar-link-badge">{item.badge}</span>
                        )}
                    </Link>
                ))}
            </nav>

            {/* Footer */}
            <div className="sidebar-footer">
                <div style={{ fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>
                    {/* Mantener sincronizado con VERSION en apps/backend/app/__init__.py */}
                    v2.4.0
                </div>
            </div>
        </aside>
    );
}
